import { useEffect, useState, useCallback } from 'react'
import { Contract } from 'ethers'
import { LYTPoolAbi } from '@shared/Abis'
import { EthAddr, ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { PoolLytOverviewCTO } from '@shared/sharedTypes'
import { logOT } from '@shared/loggingShared'
import { useOtChainState, useOtSelector, OtStoreState } from '@react/redux/store'

interface UseLytPoolBalanceOptions {
  accountAddress: EthAddr
  enabled?: boolean
  refetchInterval?: number
}

interface UseLytPoolBalanceReturn {
  maxRedeemRequest: bigint
  actualBalance: bigint
  isLoading: boolean
  error: Error | null
  refetch: () => Promise<void>
  liquidityAssetAddr: EthAddr | null
}

export const useLytPoolBalance = ({
  accountAddress,
  enabled = true,
  refetchInterval,
}: UseLytPoolBalanceOptions): UseLytPoolBalanceReturn => {
  const { chainConfiguration, stageConfiguration } = useOtChainState()
  const { websocketProvider } = chainConfiguration
  const poolAddr = stageConfiguration.lytPoolAddr

  const poolOverview = useOtSelector(
    (state: OtStoreState) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO]
  ) as PoolLytOverviewCTO | undefined

  const [maxRedeemRequest, setMaxRedeemRequest] = useState<bigint>(0n)
  const [actualBalance, setActualBalance] = useState<bigint>(0n)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)

  const liquidityAssetAddr = poolOverview?.liquidityAssetAddr || null

  const fetchBalances = useCallback(async (): Promise<void> => {
    if (!websocketProvider || !poolAddr || !accountAddress || !enabled) {
      return
    }

    try {
      setError(null)
      const lytPoolContract = new Contract(poolAddr, LYTPoolAbi, websocketProvider)

      // Fetch both maxWithdrawRequest and actual token balance
      const [maxRedeemRequestResult, tokenBalance] = await Promise.all([
        lytPoolContract.maxRedeemRequest(accountAddress),
        lytPoolContract.balanceOf(accountAddress),
      ])

      const maxRedeemRequestBigInt = BigInt(maxRedeemRequestResult)
      const tokenBalanceBigInt = BigInt(tokenBalance)

      // Set maxWithdrawRequest and actual token balance (not converted by exchange rate)
      setMaxRedeemRequest(maxRedeemRequestBigInt)
      setActualBalance(tokenBalanceBigInt)

      logOT.info('LYT Pool balances fetched:', {
        poolAddr,
        accountAddress,
        maxRedeemRequest: maxRedeemRequestBigInt.toString(),
        actualBalance: tokenBalanceBigInt.toString(),
        tokenBalance: tokenBalanceBigInt.toString(),
      })
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to fetch LYT pool balances')
      setError(error)
      logOT.error('Error fetching LYT pool balances:', error)
    } finally {
      setIsLoading(false)
    }
  }, [websocketProvider, poolAddr, accountAddress, enabled])

  const refetch = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    await fetchBalances()
  }, [fetchBalances])

  useEffect(() => {
    if (!websocketProvider || !poolAddr || !accountAddress || !enabled) {
      setIsLoading(false)
      return
    }

    let contract: Contract | null = null
    let isMounted = true
    let intervalId: NodeJS.Timeout | null = null

    const setupEventListeners = async () => {
      try {
        // Initial balance fetch
        await fetchBalances()

        if (!isMounted) return

        // Set up event listeners for LYT pool events
        contract = new Contract(poolAddr, LYTPoolAbi, websocketProvider)

        // Listen for deposit events
        const depositFilter = contract.filters.PoolDeposit()
        const redeemRequestFilter = contract.filters.RedeemRequested()
        const redeemRepayFilter = contract.filters.RedeemRepay()

        const handleLytPoolEvent = (log: any) => {
          logOT.debug('LYT Pool event detected', log)
          try {
            // Check if the event involves our account
            if (log?.args?.length > 0) {
              const eventAccountAddress = log.args[0] // First arg is usually the account address
              if (eventAccountAddress?.toLowerCase() === accountAddress.toLowerCase()) {
                logOT.info('LYT Pool event for our account detected, updating balances:', {
                  poolAddr,
                  accountAddress,
                  eventName: log.eventName || 'Unknown',
                })

                // Refetch balances after blockchain state update
                setTimeout(() => {
                  if (isMounted) {
                    fetchBalances()
                  }
                }, 1000)
              }
            }
          } catch (parseError) {
            logOT.error('Error handling LYT pool event:', parseError)
          }
        }

        contract.on(depositFilter, handleLytPoolEvent)
        contract.on(redeemRequestFilter, handleLytPoolEvent)
        contract.on(redeemRepayFilter, handleLytPoolEvent)

        // Optional polling fallback
        if (refetchInterval && refetchInterval > 0) {
          intervalId = setInterval(() => {
            if (isMounted) {
              fetchBalances()
            }
          }, refetchInterval)
        }
      } catch (err) {
        if (isMounted) {
          const error = err instanceof Error ? err : new Error('Failed to setup LYT pool event listeners')
          setError(error)
          logOT.error('Error setting up LYT pool event listeners:', error)
        }
      }
    }

    setupEventListeners()

    // Cleanup function
    return () => {
      isMounted = false

      if (contract) {
        try {
          contract.removeAllListeners()
        } catch (err) {
          logOT.error('Error cleaning up LYT pool event listeners:', err)
        }
      }

      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [websocketProvider, poolAddr, accountAddress, enabled, fetchBalances, refetchInterval])

  return {
    maxRedeemRequest,
    actualBalance,
    isLoading,
    error,
    refetch,
    liquidityAssetAddr,
  }
}
